import { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { AlertCircle, Truck, Filter } from 'lucide-react';
import { MAPBOX_TOKEN, DEFAULT_STYLE, MARKER_COLORS } from '@/lib/mapbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { subDays, startOfDay, isAfter } from 'date-fns';

interface Mission {
  id: string;
  orderNumber: string;
  pickupAddress: string;
  deliveryAddress: string;
  pickupLat?: number;
  pickupLng?: number;
  deliveryLat?: number;
  deliveryLng?: number;
  status: string;
  driverName?: string;
  vehiclePlate?: string;
  createdAt?: string;
}

interface LiveMapViewProps {
  missions: Mission[];
}

export const LiveMapView = ({ missions }: LiveMapViewProps) => {
  const { t } = useTranslation();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilters, setStatusFilters] = useState({
    assigned: true,
    in_transit: true,
    delivered: true,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const driverMarkersRef = useRef<Map<string, mapboxgl.Marker>>(new Map());

  const STATUS_COLORS = {
    assigned: '#0880AE',
    in_transit: '#FFBF07',
    delivered: '#14A38B',
  };

  // Filter missions using useMemo
  const validMissions = useMemo(() => {
    const getDateFilterDate = () => {
      const now = new Date();
      switch (dateFilter) {
        case 'today':
          return startOfDay(now);
        case '7days':
          return subDays(now, 7);
        case '30days':
          return subDays(now, 30);
        case '90days':
          return subDays(now, 90);
        case '365days':
          return subDays(now, 365);
        default:
          return null;
      }
    };

    const dateFilterDate = getDateFilterDate();

    return missions.filter((m) => {
      const matchesCoordinates = m.pickupLat && m.pickupLng && m.deliveryLat && m.deliveryLng;
      const matchesStatus = statusFilters[m.status as keyof typeof statusFilters];
      const matchesSearch = searchQuery === '' || m.orderNumber.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDate = !dateFilterDate || (m.createdAt && isAfter(new Date(m.createdAt), dateFilterDate));
      
      return matchesCoordinates && matchesStatus && matchesSearch && matchesDate;
    });
  }, [missions, statusFilters, searchQuery, dateFilter]);

  useEffect(() => {
    // Check if token is configured
    if (!MAPBOX_TOKEN) {
      setError('Mapbox token is not configured');
      setIsLoading(false);
      return;
    }

    // Check if container exists
    if (!mapContainer.current) return;

    // Initialize map
    mapboxgl.accessToken = MAPBOX_TOKEN;

    try {
      // Calculate center - use missions center if available, otherwise default to Paris
      let centerLat = 48.8566; // Paris default
      let centerLng = 2.3522;
      let defaultZoom = 6;

      if (validMissions.length > 0) {
        centerLat = validMissions.reduce((sum, m) => sum + (m.pickupLat! + m.deliveryLat!) / 2, 0) / validMissions.length;
        centerLng = validMissions.reduce((sum, m) => sum + (m.pickupLng! + m.deliveryLng!) / 2, 0) / validMissions.length;
      }

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: DEFAULT_STYLE,
        center: [centerLng, centerLat],
        zoom: defaultZoom,
      });

      // Add navigation controls
      map.current.addControl(
        new mapboxgl.NavigationControl({
          visualizePitch: false,
        }),
        'top-right'
      );

      map.current.on('load', async () => {
        if (!map.current) return;

        // Only process missions if we have valid ones
        if (validMissions.length === 0) {
          setIsLoading(false);
          return;
        }

        const bounds = new mapboxgl.LngLatBounds();

        // Add markers for each mission
        for (const mission of validMissions) {
          if (!map.current) return;

          // Pickup marker
          const pickupEl = document.createElement('div');
          pickupEl.className = 'w-10 h-10 flex items-center justify-center cursor-pointer';
          pickupEl.innerHTML = `
            <div class="w-full h-full rounded-full bg-[${MARKER_COLORS.pickup}] flex items-center justify-center shadow-lg border-2 border-white">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
              </svg>
            </div>
          `;

          new mapboxgl.Marker({ element: pickupEl })
            .setLngLat([mission.pickupLng!, mission.pickupLat!])
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<div class="p-3">
                  <p class="font-bold text-sm mb-1">${mission.orderNumber}</p>
                  <p class="font-semibold text-xs text-[#0880AE]">${t('common.pickup')}</p>
                  <p class="text-xs text-[#5A6B7A] mt-1">${mission.pickupAddress}</p>
                  ${mission.driverName ? `<p class="text-xs mt-2"><span class="font-semibold">Driver:</span> ${mission.driverName}</p>` : ''}
                  ${mission.vehiclePlate ? `<p class="text-xs"><span class="font-semibold">Vehicle:</span> ${mission.vehiclePlate}</p>` : ''}
                </div>`
              )
            )
            .addTo(map.current);

          bounds.extend([mission.pickupLng!, mission.pickupLat!]);

          // Delivery marker
          const deliveryEl = document.createElement('div');
          deliveryEl.className = 'w-10 h-10 flex items-center justify-center cursor-pointer';
          deliveryEl.innerHTML = `
            <div class="w-full h-full rounded-full bg-[${MARKER_COLORS.delivery}] flex items-center justify-center shadow-lg border-2 border-white">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
              </svg>
            </div>
          `;

          new mapboxgl.Marker({ element: deliveryEl })
            .setLngLat([mission.deliveryLng!, mission.deliveryLat!])
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<div class="p-3">
                  <p class="font-bold text-sm mb-1">${mission.orderNumber}</p>
                  <p class="font-semibold text-xs text-[#FF7171]">${t('common.delivery')}</p>
                  <p class="text-xs text-[#5A6B7A] mt-1">${mission.deliveryAddress}</p>
                  ${mission.driverName ? `<p class="text-xs mt-2"><span class="font-semibold">Driver:</span> ${mission.driverName}</p>` : ''}
                  ${mission.vehiclePlate ? `<p class="text-xs"><span class="font-semibold">Vehicle:</span> ${mission.vehiclePlate}</p>` : ''}
                </div>`
              )
            )
            .addTo(map.current);

          bounds.extend([mission.deliveryLng!, mission.deliveryLat!]);

          // Add driver/truck marker based on mission status
          let driverLat: number;
          let driverLng: number;
          let driverColor: string;
          let driverStatusText: string;

          if (mission.status === 'assigned') {
            // Driver at pickup location
            driverLat = mission.pickupLat!;
            driverLng = mission.pickupLng!;
            driverColor = MARKER_COLORS.tracking;
            driverStatusText = t('orders.statuses.assigned');
          } else if (mission.status === 'delivered') {
            // Driver at delivery location
            driverLat = mission.deliveryLat!;
            driverLng = mission.deliveryLng!;
            driverColor = '#14A38B'; // Success green
            driverStatusText = t('orders.statuses.delivered');
          } else {
            // Use pickup location as default position (placeholder until real GPS tracking)
            driverLat = mission.pickupLat!;
            driverLng = mission.pickupLng!;
            driverColor = MARKER_COLORS.current;
            driverStatusText = t('orders.statuses.in_transit');
          }

          const driverEl = document.createElement('div');
          driverEl.className = 'w-12 h-12 flex items-center justify-center cursor-pointer';
          driverEl.innerHTML = `
            <div class="w-full h-full rounded-full bg-[${driverColor}] flex items-center justify-center shadow-xl border-3 border-white">
              <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 18.5a1.5 1.5 0 01-1 1.415V21a1 1 0 01-2 0v-1.085a1.5 1.5 0 010-2.83V16h-6v1.085a1.5 1.5 0 010 2.83V21a1 1 0 01-2 0v-1.085A1.5 1.5 0 016 18.5H5a1 1 0 010-2h1v-6H5a1 1 0 010-2h1a1.5 1.5 0 011-1.415V6a1 1 0 012 0v1.085a1.5 1.5 0 010 2.83V11h6V9.915a1.5 1.5 0 010-2.83V6a1 1 0 012 0v1.085A1.5 1.5 0 0119 8.5h1a1 1 0 010 2h-1v6h1a1 1 0 010 2h-1zm-2-2h-8v-6h8v6z"/>
              </svg>
            </div>
          `;

          const driverMarker = new mapboxgl.Marker({ element: driverEl })
            .setLngLat([driverLng, driverLat])
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<div class="p-3">
                  <p class="font-bold text-sm mb-1">${mission.orderNumber}</p>
                  <p class="font-semibold text-xs" style="color: ${driverColor}">${driverStatusText}</p>
                  ${mission.driverName ? `<p class="text-xs mt-2"><span class="font-semibold">Driver:</span> ${mission.driverName}</p>` : ''}
                  ${mission.vehiclePlate ? `<p class="text-xs"><span class="font-semibold">Vehicle:</span> ${mission.vehiclePlate}</p>` : ''}
                </div>`
              )
            )
            .addTo(map.current);

          // Store marker reference for real-time updates
          driverMarkersRef.current.set(mission.id, driverMarker);

          // Fetch and draw route using Mapbox Directions API
          const lineId = `route-line-${mission.id}`;
          const routeColor = STATUS_COLORS[mission.status as keyof typeof STATUS_COLORS] ?? '#0880AE';
          console.log('🗺️ Mission:', mission.id, 'Status:', mission.status, 'Route color:', routeColor);
          
          try {
            // Fetch route geometry from Mapbox Directions API
            const routeUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${mission.pickupLng},${mission.pickupLat};${mission.deliveryLng},${mission.deliveryLat}?geometries=geojson&access_token=${MAPBOX_TOKEN}`;
            const response = await fetch(routeUrl);
            
            if (response.ok) {
              const data = await response.json();
              
              if (data.routes && data.routes.length > 0) {
                // Use real route geometry
                map.current?.addSource(lineId, {
                  type: 'geojson',
                  data: {
                    type: 'Feature',
                    properties: {},
                    geometry: data.routes[0].geometry,
                  },
                });

                map.current?.addLayer({
                  id: lineId,
                  type: 'line',
                  source: lineId,
                  layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                  },
                  paint: {
                    'line-color': routeColor,
                    'line-width': 3,
                    'line-opacity': 0.8,
                  },
                });
              } else {
                // Fallback to straight line if no route found
                throw new Error('No route found');
              }
            } else {
              // Fallback to straight line if API fails
              throw new Error('API request failed');
            }
          } catch (error) {
            console.warn(`Failed to fetch route for mission ${mission.orderNumber}, using fallback:`, error);
            
            // Fallback: Draw straight line
            map.current?.addSource(lineId, {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: [
                    [mission.pickupLng!, mission.pickupLat!],
                    [mission.deliveryLng!, mission.deliveryLat!],
                  ],
                },
              },
            });

            map.current?.addLayer({
              id: lineId,
              type: 'line',
              source: lineId,
              layout: {
                'line-join': 'round',
                'line-cap': 'round',
              },
              paint: {
                'line-color': routeColor,
                'line-width': 2,
                'line-opacity': 0.6,
                'line-dasharray': [2, 2],
              },
            });
          }
        }

        // Fit map to show all markers (only if we have missions)
        if (!bounds.isEmpty()) {
          map.current.fitBounds(bounds, {
            padding: 80,
            maxZoom: 12,
          });
        }

        setIsLoading(false);
      });

      map.current.on('error', (e) => {
        console.error('Map error:', e);
        setError('Failed to load map');
        setIsLoading(false);
      });
    } catch (error) {
      console.error('Map initialization error:', error);
      setError('Failed to initialize map');
      setIsLoading(false);
    }

    // Cleanup
    return () => {
      map.current?.remove();
      map.current = null;
      driverMarkersRef.current.clear();
    };
  }, [validMissions, t]);

  // Real-time tracking updates
  useEffect(() => {
    if (missions.length === 0) return;

    const orderIds = missions.map((m) => m.id);
    
    const channel = supabase
      .channel('live-map-tracking')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tracking_events',
          filter: `order_id=in.(${orderIds.join(',')})`,
        },
        (payload: any) => {
          const { order_id, latitude, longitude, event_type } = payload.new;
          
          // Update driver marker position if coordinates are available
          if (latitude && longitude) {
            const marker = driverMarkersRef.current.get(order_id);
            if (marker) {
              marker.setLngLat([Number(longitude), Number(latitude)]);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [missions]);

  // Show error state
  if (error) {
    return (
      <div className="bg-[#EBF4F8] rounded-xl p-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 z-10 bg-[#EBF4F8] rounded-xl">
          <Skeleton className="w-full h-full rounded-xl" />
        </div>
      )}

      <div
        ref={mapContainer}
        className="w-full h-[calc(100vh-12rem)] rounded-xl shadow-lg"
      />

      {/* Filters Panel */}
      {!isLoading && (
        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-4 z-10 space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b">
            <Filter size={18} className="text-[#0880AE]" />
            <span className="font-semibold text-[#2D3748]">{t('liveMap.filters')}</span>
          </div>

          {/* Search Bar */}
          <div>
            <Input
              type="text"
              placeholder={t('liveMap.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-3 py-2 text-sm"
            />
          </div>

          {/* Date Filter */}
          <div>
            <Label className="text-xs text-[#5A6B7A] mb-2 block">{t('liveMap.dateFilter')}</Label>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-full text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('liveMap.allOrders')}</SelectItem>
                <SelectItem value="today">{t('liveMap.today')}</SelectItem>
                <SelectItem value="7days">{t('liveMap.last7Days')}</SelectItem>
                <SelectItem value="30days">{t('liveMap.last30Days')}</SelectItem>
                <SelectItem value="90days">{t('liveMap.last90Days')}</SelectItem>
                <SelectItem value="365days">{t('liveMap.last365Days')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Checkbox
                id="filter-assigned"
                checked={statusFilters.assigned}
                onCheckedChange={(checked) =>
                  setStatusFilters((prev) => ({ ...prev, assigned: checked as boolean }))
                }
              />
              <Label htmlFor="filter-assigned" className="flex items-center gap-2 cursor-pointer">
                <Badge style={{ backgroundColor: STATUS_COLORS.assigned }} className="text-white">
                  {t('orders.statuses.assigned')}
                </Badge>
              </Label>
            </div>

            <div className="flex items-center gap-3">
              <Checkbox
                id="filter-in-transit"
                checked={statusFilters.in_transit}
                onCheckedChange={(checked) =>
                  setStatusFilters((prev) => ({ ...prev, in_transit: checked as boolean }))
                }
              />
              <Label htmlFor="filter-in-transit" className="flex items-center gap-2 cursor-pointer">
                <Badge style={{ backgroundColor: STATUS_COLORS.in_transit }} className="text-white">
                  {t('orders.statuses.in_transit')}
                </Badge>
              </Label>
            </div>

            <div className="flex items-center gap-3">
              <Checkbox
                id="filter-delivered"
                checked={statusFilters.delivered}
                onCheckedChange={(checked) =>
                  setStatusFilters((prev) => ({ ...prev, delivered: checked as boolean }))
                }
              />
              <Label htmlFor="filter-delivered" className="flex items-center gap-2 cursor-pointer">
                <Badge style={{ backgroundColor: STATUS_COLORS.delivered }} className="text-white">
                  {t('orders.statuses.delivered')}
                </Badge>
              </Label>
            </div>
          </div>

          <div className="pt-2 border-t">
            <div className="flex items-center gap-2">
              <Truck size={16} className="text-[#5A6B7A]" />
              <span className="text-sm text-[#5A6B7A]">
                {validMissions.length} {t('nav.missions')}
              </span>
            </div>
            {validMissions.length === 0 && (
              <p className="text-xs text-[#5A6B7A] mt-2">
                {t('liveMap.noResults')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
